import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Table from '../../components/Table';
import { TrashCan } from '../../components/Icons';
import { Company, useCreateCompany, useGetCompanies } from '../../queries/Companies';
import { domain } from '../../d-man';
import { useCurrentProjectId } from '../../queries/Accounts';
import { useGetPermissions } from '../../queries/Permissions';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function Companies(props: RouteComponentProps) {
    const companies = useGetCompanies();

    const companiesRef = useRef(companies);
    const newCompany = useCreateCompany();

    const [isLoading, setIsLoading] = useState(false);

    const [companyErrorMessage, setErrorMessage] = useState('');
    const [name, setCompanyName] = useState('');
    const [displayName, setDisplayName] = useState(undefined as string | undefined | null);
    const [vatNo, setVatNo] = useState(undefined as string | undefined | null);
    const [registrationNo, setRegistrationNo] = useState(undefined as string | undefined | null);

    const projectId = useCurrentProjectId();
    const getPermissions = useGetPermissions('PROJECT', projectId.data);
    const { navigate } = props;
    // REDIRECT IF NOT CORRECT ROLE
    if (
        getPermissions.data &&
        !getPermissions.data?.items.find((p) => p.code === 'Project_Allow_Admin')?.hasAccess &&
        navigate
    ) {
        navigate(`/`);
    }

    useEffect(() => {
        companiesRef.current.reset();
    }, []);

    useEffect(() => {
        if (newCompany.error) {
            setErrorMessage(newCompany.error || 'Failed to create new company');
        }
    }, [newCompany.error]);

    const post = async () => {
        setIsLoading(true);
        setErrorMessage('');
        if (!name) {
            setErrorMessage('Please capture a Company Name');
        } else {
            await newCompany.execute({
                name,
                displayName,
                vatNo,
                registrationNo,
            });
            companies.reset();
        }
        setIsLoading(false);
    };

    const clearFields = () => {
        setErrorMessage('');
        setCompanyName('');
    };

    const deleteCompany = async (id: string | undefined) => {
        const remove = domain.delete(`/companies/${id}`);
        await remove.execute();
        companies.reset();
    };

    const toDetail = (id?: string) => {
        if (props.navigate && id) {
            props.navigate(`/companies/${id}`);
        }
    };

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-wrap items-end justify-between gap-4 bg-surface border border-border rounded-xl m-4 p-4">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="text-sm font-semibold text-foreground pb-2 pr-4 border-r border-border">
                        Add New Company
                    </div>
                    <div className="w-48">
                        <Input
                            label="Name"
                            value={name}
                            type="text"
                            onChange={(event) => setCompanyName(event.target.value)}
                        />
                    </div>
                    <div className="w-48">
                        <Input
                            label="Display Name"
                            value={displayName || ''}
                            type="text"
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                    </div>
                    <div className="w-48">
                        <Input
                            label="Vat Number"
                            value={vatNo || ''}
                            type="text"
                            onChange={(e) => setVatNo(e.target.value)}
                        />
                    </div>
                    <div className="w-48">
                        <Input
                            label="Registration Number"
                            value={registrationNo || ''}
                            type="text"
                            onChange={(e) => setRegistrationNo(e.target.value)}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {companyErrorMessage && (
                        <div className="text-sm text-danger">{companyErrorMessage}</div>
                    )}
                    <Button secondary onClick={clearFields}>
                        Clear
                    </Button>
                    <Button disabled={isLoading} onClick={post}>
                        {!isLoading ? 'Submit' : <div>Loading</div>}
                    </Button>
                </div>
            </div>
            <div className="m-4">
                <Table
                    loading={companies.loading && 'Fetching Companies from server'}
                    error={
                        companies.error &&
                        `Failed to fetch Company list from the server (${companies.error})`
                    }
                    data={companies.data?.items}
                    columns={[
                        {
                            key: 'name',
                            title: 'Company Name',
                            onClick: (company: Company) => toDetail(company.id),
                        },
                        {
                            key: 'displayName',
                            title: 'Display Name',
                            onClick: (company: Company) => toDetail(company.id),
                        },
                        {
                            key: 'vatNo',
                            title: 'VAT No',
                            onClick: (company: Company) => toDetail(company.id),
                        },
                        {
                            key: 'registrationNo',
                            title: 'Registration No',
                            onClick: (company: Company) => toDetail(company.id),
                        },
                        {
                            key: 'delete',
                            title: '',
                            onClick: (company: Company) => deleteCompany(company.id),
                            render: (company: Company) => (
                                <TrashCan
                                    className="cursor-pointer flex items-center"
                                    id={company.id}
                                />
                            ),
                        },
                    ]}
                />
            </div>
        </div>
    );
}
